// ============================================================================
// PairsForm - Mobile Bottom Navigation
// ============================================================================
// Barre fixe en bas sur mobile, ADAPTATIVE par profil (Phase 1 refonte
// navigation, N116 résorbée le 2026-08-05) :
//   pilote      → Projets · Actions · Messages · Plus   (Calendrier retiré
//                 N198 : sa route redirigeait vers /projets — doublon)
//   non-pilote  → Accueil · Mes groupes · Messages · Plus  (espace personnel)
// « Mes groupes » pointe /mes-groupes (participant) ou
// /espace-animateur/groupes (animateur pur). Le tiroir « Plus » reflète le
// menu complet (menuSections) et suit donc la refonte tout seul.
// ============================================================================

import { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Chip,
  IconButton,
  Badge,
} from "@mui/material";
import {
  FolderKanban,
  MessageCircle,
  Menu,
  X,
  Home,
  User,
  Users,
  UserCog,
  Building2,
  Settings,
  Package,
  Mail,
  ClipboardList,
  FlaskConical,
  Shield,
  Building,
  Inbox,
  BarChart3,
  GraduationCap,
  Target,
  FileText,
  LogOut,
  type LucideIcon,
} from "lucide-react";
import { useNavigation } from "../../hooks/useNavigation";
import { collecterHrefsMenu, estHrefActif } from "../../lib/navigation/menu-actif";
import { useAuth } from "../../contexts/AuthContext";
import { useUnreadMessages } from "../../hooks/useUnreadMessages";
import { ROLE_LABELS } from "../../lib/navigation/types";
import { urlOrg } from "../../lib/urls";

interface BottomTab {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Porte le badge « messages non lus ». */
  avecBadgeMessages?: boolean;
}

export function MobileNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { menuSections, user } = useNavigation();
  // Surbrillance : UN SEUL item actif — même règle que la Sidebar (menu-actif).
  const tousLesHrefs = useMemo(() => collecterHrefsMenu(menuSections), [menuSections]);
  const { logout } = useAuth();
  const unreadCount = useUnreadMessages();
  const [moreOpen, setMoreOpen] = useState(false);

  const isPilote = user?.roles?.includes("PILOTE") || user?.roles?.includes("SUPER_ADMIN");
  // Orgs 100 % Inter (CECODEV, INTER) : plus d'onglet « Projets » (lot 8.2,
  // décision A — même règle que `orgInterOnly` du menu). Le premier onglet est
  // la porte Inter de l'org : hub Formation pour cecodev, Vue d'ensemble pour
  // la marketplace.
  const orgInterOnly = user?.organisationType === "CECODEV" || user?.organisationType === "INTER";
  const premierOngletPilote: BottomTab = !orgInterOnly
    ? { label: "Projets", href: urlOrg("/projets"), icon: FolderKanban }
    : user?.organisationType === "CECODEV"
      ? { label: "Formation", href: urlOrg("/formation"), icon: GraduationCap }
      : { label: "Inter", href: "/plateforme/dashboard?vitrine=codev", icon: BarChart3 };
  const tabs: BottomTab[] = isPilote
    ? [
        premierOngletPilote,
        // « Calendrier » pointait /projets/calendrier, route redirigée vers
        // /projets depuis le pipeline simplifié — deux onglets pour la même
        // page (N198). Le cockpit d'actions est l'écran pilote le plus utile
        // en mobilité.
        { label: "Actions", href: urlOrg(""), icon: ClipboardList },
        { label: "Messages", href: "/mon-espace/echanges", icon: MessageCircle, avecBadgeMessages: true },
      ]
    : [
        { label: "Accueil", href: "/mon-espace", icon: Home },
        // Même mot que le menu (palier 0 du lot 9, 2026-10-17) : « Mes
        // inscriptions » pour la casquette participant, « Mes groupes » pour
        // l'animateur sans inscription.
        {
          label: user?.estParticipant ? "Mes inscriptions" : "Mes groupes",
          href: user?.estParticipant ? "/mon-espace/inscriptions" : "/mon-espace/animation/groupes",
          icon: GraduationCap,
        },
        { label: "Messages", href: "/mon-espace/echanges", icon: MessageCircle, avecBadgeMessages: true },
      ];

  // Préfixe le plus long qui matche — /mes-seances/communication doit allumer
  // « Messages », pas « Accueil » (/mes-seances).
  let activeIndex = -1;
  let bestLen = 0;
  tabs.forEach((t, i) => {
    const match = location.pathname === t.href || location.pathname.startsWith(t.href + "/");
    if (match && t.href.length > bestLen) {
      activeIndex = i;
      bestLen = t.href.length;
    }
  });

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue < tabs.length) {
      navigate(tabs[newValue].href);
    } else {
      setMoreOpen(true);
    }
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <Paper
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          borderTop: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
        }}
        elevation={8}
      >
        <BottomNavigation
          value={activeIndex}
          onChange={handleChange}
          showLabels
          sx={{
            height: 64,
            "& .MuiBottomNavigationAction-root": {
              minWidth: 0,
              py: 1,
              color: "text.secondary",
              "&.Mui-selected": {
                color: "#E1512C",
              },
            },
            "& .MuiBottomNavigationAction-label": {
              fontSize: "0.65rem",
              "&.Mui-selected": {
                fontSize: "0.7rem",
              },
            },
          }}
        >
          {tabs.map((tab) => (
            <BottomNavigationAction
              key={tab.href}
              label={tab.label}
              icon={
                tab.avecBadgeMessages ? (
                  <Badge
                    badgeContent={unreadCount}
                    color="error"
                    max={99}
                    sx={{ "& .MuiBadge-badge": { fontSize: "0.6rem", minWidth: 16, height: 16, padding: "0 4px" } }}
                  >
                    <tab.icon size={22} />
                  </Badge>
                ) : (
                  <tab.icon size={22} />
                )
              }
            />
          ))}
          <BottomNavigationAction
            label="Plus"
            icon={<Menu size={22} />}
          />
        </BottomNavigation>
      </Paper>

      {/* "Plus" Drawer */}
      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "80vh",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Handle bar */}
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: "divider",
              borderRadius: 2,
              mx: "auto",
              mb: 2,
            }}
          />

          {/* User info */}
          {user && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, px: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {user.prenom} {user.nom}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.organisationNom || "Organisation"}
                </Typography>
              </Box>
              <Chip
                label={ROLE_LABELS[user.roles[0]]}
                size="small"
                color="primary"
                sx={{ fontSize: "0.7rem", height: 22 }}
              />
              <IconButton size="small" onClick={() => setMoreOpen(false)}>
                <X size={20} />
              </IconButton>
            </Box>
          )}

          <Divider sx={{ mb: 1 }} />

          {/* Menu items */}
          <List disablePadding>
            {menuSections.map((section) => (
              <Box key={section.id}>
                {/* Section avec lien direct */}
                {section.href && !section.items && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        navigate(section.href!);
                        setMoreOpen(false);
                      }}
                      selected={estHrefActif(section.href!, location.pathname, tousLesHrefs, location.search)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        "&.Mui-selected": {
                          bgcolor: "rgba(225, 81, 44, 0.08)",
                          "& .MuiListItemIcon-root": { color: "#E1512C" },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <section.icon size={20} />
                      </ListItemIcon>
                      <ListItemText primary={section.title} />
                      {section.badge != null && section.badge > 0 && (
                        <Chip
                          label={section.badge > 99 ? "99+" : section.badge}
                          size="small"
                          color="error"
                          sx={{ height: 18, fontSize: "0.65rem", minWidth: 18 }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                )}

                {/* Section avec sous-items */}
                {section.items && (
                  <>
                    {section.separator && (
                      <Divider sx={{ my: 1 }} />
                    )}
                    <Typography
                      variant="overline"
                      sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary", fontSize: "0.65rem" }}
                    >
                      {section.title}
                    </Typography>
                    {section.items
                      .filter(item => !item.disabled)
                      .map((item) => (
                        <ListItem key={item.id} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              navigate(item.href);
                              setMoreOpen(false);
                            }}
                            selected={estHrefActif(item.href, location.pathname, tousLesHrefs, location.search)}
                            sx={{
                              borderRadius: 2,
                              mb: 0.25,
                              py: 0.75,
                              "&.Mui-selected": {
                                bgcolor: "rgba(225, 81, 44, 0.08)",
                                "& .MuiListItemIcon-root": { color: "#E1512C" },
                              },
                            }}
                          >
                            {item.icon && (
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <item.icon size={18} />
                              </ListItemIcon>
                            )}
                            <ListItemText
                              primary={item.label}
                              primaryTypographyProps={{ fontSize: "0.875rem" }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                  </>
                )}
              </Box>
            ))}
          </List>

          <Divider sx={{ my: 1 }} />

          {/* Logout */}
          <ListItemButton
            onClick={() => {
              setMoreOpen(false);
              logout();
            }}
            sx={{ borderRadius: 2, color: "error.main" }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "error.main" }}>
              <LogOut size={20} />
            </ListItemIcon>
            <ListItemText primary="Déconnexion" />
          </ListItemButton>
        </Box>
      </Drawer>
    </>
  );
}
